import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Send, MessageCircle, Loader2, ArrowLeft, Ban, Flag, X, Video, EyeOff, Eye, ShieldAlert } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { CATEGORIES } from "@/lib/categories";
import { getUser } from "@/lib/user";
import { ReportModal } from "@/components/ReportModal";
import { VideoCallModal } from "@/components/VideoCallModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const heure = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });


export default function Messages() {
  const user = getUser();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(params.get("conv") || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [appelVideo, setAppelVideo] = useState(null); // {disponible, salle}
  const [enAppel, setEnAppel] = useState(false);
  const [blind, setBlind] = useState(null); // statut Blind Date de la conversation
  const bottomRef = useRef(null);

  const loadBlocked = useCallback(() => {
    if (user) axios.get(`${API}/blocages`, { params: { profile_id: user.id } }).then((r) => setBlocked(r.data));
  }, [user?.id]);

  useEffect(() => { loadBlocked(); }, [loadBlocked]);

  const loadConvs = useCallback(() => {
    if (user) axios.get(`${API}/conversations`, { params: { profile_id: user.id } }).then((r) => setConvs(r.data));
  }, [user?.id]);

  const loadMessages = useCallback(() => {
    if (user && selected) {
      axios
        .get(`${API}/conversations/${selected}/messages`, { params: { profile_id: user.id } })
        .then((r) => setMessages(r.data))
        .catch(() => setMessages([]));
    }
  }, [user?.id, selected]);

  // création auto de conversation via ?to=
  useEffect(() => {
    const to = params.get("to");
    if (user && to && to !== user.id) {
      axios
        .post(`${API}/conversations`, { from_id: user.id, to_id: to })
        .then((r) => {
          setSelected(r.data.id);
          setParams({ conv: r.data.id }, { replace: true });
          loadConvs();
        })
        .catch((err) => {
          if (err.response?.status === 403) {
            toast.error("La messagerie est réservée aux abonnés.");
            navigate("/abonnement");
          } else {
            toast.error(err.response?.data?.detail || "Erreur");
          }
        });
    }
  }, []);

  useEffect(() => {
    loadConvs();
    const t = setInterval(loadConvs, 5000);
    return () => clearInterval(t);
  }, [loadConvs]);

  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, 3000);
    return () => clearInterval(t);
  }, [loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // éligibilité appel vidéo (match mutuel + catégories Love/VIP)
  useEffect(() => {
    setAppelVideo(null);
    setEnAppel(false);
    if (user && selected) {
      axios
        .get(`${API}/conversations/${selected}/appel-video`, { params: { profile_id: user.id } })
        .then((r) => setAppelVideo(r.data))
        .catch(() => setAppelVideo(null));
    }
  }, [user?.id, selected]);

  // statut Blind Date (progression et révélation mises à jour à chaque message)
  useEffect(() => {
    if (user && selected) {
      axios
        .get(`${API}/conversations/${selected}/blind-date`, { params: { profile_id: user.id } })
        .then((r) => setBlind(r.data))
        .catch(() => setBlind(null));
    } else {
      setBlind(null);
    }
  }, [user?.id, selected, messages.length]);

  const revelerPhoto = async () => {
    try {
      const res = await axios.post(`${API}/conversations/${selected}/blind-date/reveler`, { profile_id: user.id });
      setBlind(res.data);
      toast.success(res.data.revele ? "Photos révélées !" : "Accord enregistré", {
        description: res.data.revele ? "Vous pouvez maintenant voir vos photos nettes." : "La photo sera révélée dès que l'autre personne acceptera aussi.",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // temps réel : les nouveaux messages arrivent instantanément via WebSocket
  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    if (!user) return;
    const ws = new WebSocket(`${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws?profile_id=${user.id}`);
    ws.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.type === "message") {
          loadConvs();
          if (d.conversation_id === selectedRef.current) {
            setMessages((prev) => (prev.some((m) => m.id === d.message.id) ? prev : [...prev, d.message]));
          }
        }
      } catch {}
    };
    return () => ws.close();
  }, [user?.id, loadConvs]);

  const envoyer = async (e) => {
    e.preventDefault();
    if (!text.trim() || !selected) return;
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${selected}/messages`, { sender_id: user.id, text });
      setText("");
      loadMessages();
      loadConvs();
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail?.includes("abonnés")) {
        toast.error("La messagerie est réservée aux abonnés.");
        navigate("/abonnement");
      } else if (err.response?.status === 403) {
        toast.error(err.response?.data?.detail || "Envoi impossible.");
      } else {
        toast.error(err.response?.data?.detail || "Erreur d'envoi");
      }
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return (
      <div className="bg-[#FAF8F5] min-h-screen">
        <Navbar />
        <main className="max-w-xl mx-auto px-4 pt-40 pb-24 text-center">
          <Lock size={44} className="mx-auto text-[#E05638] mb-6" />
          <h1 className="font-heading text-3xl font-extrabold text-stone-900">Créez d'abord votre profil</h1>
          <p className="text-stone-600 mt-4">La messagerie est liée à votre profil FodindinCa.</p>
          <Link to="/inscription" data-testid="messages-register-link" className="mt-8 inline-block bg-[#E05638] hover:bg-[#C84428] text-white font-semibold px-8 py-4 rounded-full transition-colors">
            Créer mon profil gratuit
          </Link>
        </main>
      </div>
    );
  }

  const convActive = convs.find((c) => c.id === selected);
  const jeBloque = convActive && blocked.includes(convActive.autre?.id);

  const toggleBlocage = async () => {
    if (!convActive) return;
    try {
      if (jeBloque) {
        await axios.delete(`${API}/blocages`, { params: { profile_id: user.id, cible_id: convActive.autre.id } });
        toast.success(`${convActive.autre.firstname} débloqué(e).`);
      } else {
        await axios.post(`${API}/blocages`, { profile_id: user.id, cible_id: convActive.autre.id });
        toast.success(`${convActive.autre.firstname} bloqué(e).`, { description: "Vous ne recevrez plus ses messages." });
      }
      loadBlocked();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="bg-[#FAF8F5] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 flex flex-col">
        <h1 className="font-heading text-3xl font-extrabold text-stone-900 mb-6">Messagerie</h1>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 min-h-[520px]">
          {/* liste conversations */}
          <div className={`bg-white border border-[#E5DFD5] rounded-2xl overflow-hidden flex-col ${selected ? "hidden md:flex" : "flex"}`} data-testid="conversations-list">
            <div className="px-5 py-4 border-b border-[#E5DFD5] font-heading font-bold text-stone-900 text-sm">Conversations</div>
            <div className="flex-1 overflow-y-auto">
              {convs.length === 0 ? (
                <div className="p-6 text-center text-sm text-stone-500">
                  <MessageCircle size={28} className="mx-auto mb-3 text-stone-300" />
                  Aucune conversation. Envoyez un Flash sur un profil pour démarrer !
                </div>
              ) : (
                convs.map((c) => {
                  const cat = CATEGORIES[c.autre?.category] || CATEGORIES.lago;
                  return (
                    <button
                      key={c.id}
                      onClick={() => { setSelected(c.id); setParams({ conv: c.id }, { replace: true }); }}
                      data-testid={`conversation-item-${c.id}`}
                      className={`w-full text-left px-5 py-4 border-b border-[#F3EFEA] hover:bg-[#FAF8F5] transition-colors ${selected === c.id ? "bg-[#FAF8F5]" : ""}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-10 h-10 rounded-full flex items-center justify-center text-white font-heading font-bold shrink-0" style={{ backgroundColor: cat.color }}>
                          {c.autre?.firstname?.charAt(0)}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-stone-900">{c.autre?.firstname} <span className="text-xs text-stone-400 font-normal">· {c.autre?.city}</span></div>
                          <div className="text-xs text-stone-500 truncate">{c.last_message || "Nouvelle conversation"}</div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* fil de discussion */}
          <div className={`bg-white border border-[#E5DFD5] rounded-2xl flex-col ${selected ? "flex" : "hidden md:flex"}`} data-testid="chat-thread">
            {!convActive ? (
              <div className="flex-1 flex flex-col items-center justify-center text-stone-400 text-sm gap-3">
                <MessageCircle size={36} className="text-stone-300" />
                Sélectionnez une conversation
              </div>
            ) : (
              <>
                <div className="px-5 py-4 border-b border-[#E5DFD5] flex items-center gap-3">
                  <button className="md:hidden text-stone-500" onClick={() => setSelected(null)} data-testid="chat-back">
                    <ArrowLeft size={18} />
                  </button>
                  <span className="font-heading font-bold text-stone-900">{convActive.autre?.firstname}</span>
                  <span className="text-xs text-stone-400">{convActive.autre?.city}</span>
                  <div className="ml-auto flex items-center gap-1">
                    {appelVideo?.disponible && !jeBloque && (
                      <button
                        onClick={() => setEnAppel(true)}
                        title="Appel vidéo"
                        data-testid="chat-video-call-btn"
                        className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#0A5C36] hover:bg-[#084a2b] text-white transition-colors mr-1"
                      >
                        <Video size={15} /> <span className="hidden sm:inline">Appel vidéo</span>
                      </button>
                    )}
                    <button
                      onClick={() => setShowReport(true)}
                      title="Signaler"
                      data-testid="chat-report-btn"
                      className="p-2 rounded-full text-stone-400 hover:text-[#E05638] hover:bg-[#E05638]/10 transition-colors"
                    >
                      <Flag size={16} />
                    </button>
                    <button
                      onClick={toggleBlocage}
                      title={jeBloque ? "Débloquer" : "Bloquer"}
                      data-testid="chat-block-btn"
                      className={`p-2 rounded-full transition-colors ${jeBloque ? "text-white bg-stone-800 hover:bg-stone-700" : "text-stone-400 hover:text-stone-800 hover:bg-stone-100"}`}
                    >
                      <Ban size={16} />
                    </button>
                  </div>
                </div>
                {blind?.actif && (
                  <div className={`px-5 py-3 border-b border-[#E5DFD5] text-xs flex flex-wrap items-center gap-2 ${blind.revele ? "bg-emerald-50 text-emerald-800" : "bg-[#9f2558]/5 text-stone-600"}`} data-testid="blind-date-banner">
                    {blind.revele ? (
                      <span className="flex items-center gap-2" data-testid="blind-date-revele">
                        <Eye size={14} className="text-emerald-700" />
                        <b>Blind Date :</b> les photos sont maintenant révélées !
                      </span>
                    ) : (
                      <>
                        <EyeOff size={14} className="text-[#9f2558] shrink-0" />
                        <span>
                          <b>Mode Blind Date :</b> la photo se révélera après quelques échanges — <b data-testid="blind-date-progress">{blind.messages}/{blind.seuil}</b> messages
                        </span>
                        <span className="ml-auto">
                          {blind.mon_accord ? (
                            <span className="italic text-stone-400" data-testid="blind-date-attente">En attente de l'accord de {convActive.autre?.firstname}…</span>
                          ) : (
                            <button onClick={revelerPhoto} data-testid="blind-date-reveler-btn" className="font-semibold text-[#9f2558] hover:underline">
                              Révéler maintenant (accord mutuel)
                            </button>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                )}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 max-h-[440px]">
                  {messages.map((m) => (
                    <div key={m.id}>
                      <div className={`flex ${m.sender_id === user.id ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                            m.sender_id === user.id ? "bg-[#E05638] text-white rounded-br-md" : "bg-[#F3EFEA] text-stone-800 rounded-bl-md"
                          }`}
                          data-testid={`message-${m.id}`}
                        >
                          {m.text}
                          <div className={`text-[10px] mt-1 ${m.sender_id === user.id ? "text-white/70" : "text-stone-400"}`}>{heure(m.created_at)}</div>
                        </div>
                      </div>
                      {m.alerte_securite && m.sender_id !== user.id && (
                        <div className="flex justify-start mt-1.5" data-testid={`alerte-securite-${m.id}`}>
                          <div className="max-w-[85%] flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                            <ShieldAlert size={13} className="shrink-0 mt-0.5 text-amber-600" />
                            <span>
                              Par sécurité, n'envoyez jamais d'argent à quelqu'un que vous n'avez pas rencontré.{" "}
                              <button
                                onClick={() => setShowReport(true)}
                                data-testid={`alerte-securite-signaler-${m.id}`}
                                className="font-semibold underline hover:text-amber-900"
                              >
                                Signalez tout comportement suspect
                              </button>
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
                {jeBloque ? (
                  <div className="p-4 border-t border-[#E5DFD5] bg-stone-50 text-center text-sm text-stone-600" data-testid="chat-blocked-banner">
                    <Ban size={15} className="inline mr-1.5 -mt-0.5" />
                    Vous avez bloqué {convActive.autre?.firstname}.{" "}
                    <button onClick={toggleBlocage} data-testid="chat-unblock-link" className="font-semibold text-[#E05638] hover:underline">Débloquer</button>
                  </div>
                ) : (
                <form onSubmit={envoyer} className="p-4 border-t border-[#E5DFD5] flex gap-3">
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Écrivez votre message..."
                    data-testid="chat-input"
                    className="flex-1 bg-[#FAF8F5] border border-[#E5DFD5] rounded-full px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05638]/50"
                  />
                  <button
                    type="submit"
                    disabled={sending || !text.trim()}
                    data-testid="chat-send"
                    className="bg-[#E05638] hover:bg-[#C84428] disabled:opacity-40 text-white rounded-full w-12 h-12 flex items-center justify-center transition-colors"
                  >
                    {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  </button>
                </form>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      {showReport && convActive && (
        <ReportModal autre={convActive.autre} convId={convActive.id} userId={user.id} onClose={() => setShowReport(false)} />
      )}
      {enAppel && appelVideo?.salle && convActive && (
        <VideoCallModal
          salle={appelVideo.salle}
          displayName={user.firstname}
          autreNom={convActive.autre?.firstname}
          onClose={() => setEnAppel(false)}
        />
      )}
    </div>
  );
}
